/** @typedef {'en'|'fr'} AppLocale */

/** @type {Record<AppLocale, Record<string, string>>} */
export const translations = {
  en: {
    tab_home: "Home",
    tab_progress: "Progress",
    tab_insight: "Insight",
    tab_library: "Library",
    tab_community: "Community",
    tab_screentime: "Screen Time",

    lang_english: "English",
    lang_french: "Français",
    lang_label: "Language",

    menu_title: "Menu",
    menu_close: "Close",
    menu_help: "Help",
    menu_notifications: "Notifications",

    library_title: "Library",
    library_subtitle: "Articles, recommended videos, and books to support your journey.",
    library_articles: "Articles",
    library_videos: "Recommended Videos",
    library_books: "Books that change lives",
    library_back: "← Back to library",
    library_open_link: "Open full article",
    library_min_read: "min read",
    library_empty_articles: "No articles in this language yet.",
    library_empty_videos: "No videos in this language yet.",
    library_link_error_title: "Link",
    library_link_error_body: "Could not open this link.",

    video_loading: "Loading video…",
    video_unavailable: "This video could not be loaded.",
    video_open_youtube: "Open on YouTube",

    community_title: "Community",
    community_join_title: "Join the community",
    community_intro_join:
      "Procsystim community is for members of the app. Choose a nickname, share your experiences, and encourage others who are on the same path. Never use your real name.",
    community_intro_feed:
      "Read stories from other members, share what you learn, and encourage someone today.",
    community_signed_in: "Signed in as",
    community_nickname: "Your User ID (nickname)",
    community_nickname_hint: "Letters, numbers, underscores. No spaces. 3–24 characters.",
    community_join_btn: "Join community",
    community_joining: "Joining…",
    community_join_error_title: "Could not join",
    community_nickname_taken_title: "Nickname taken",
    community_nickname_taken_body: "That nickname is already taken. Try a different one.",
    community_waking_title: "Server is waking up",
    community_waking_body:
      "The server takes up to 30 seconds to wake up. Please wait a moment and try again.",
    community_share: "+ Share",
    community_loading: "Loading…",
    community_back: "Back",
    community_welcome: "Welcome",
    community_welcome_body:
      "You're in as @{id}. Share honestly — use a nickname, never your real name.",
    community_empty: "No posts yet. Be the first to share your experience.",
    community_posted: "Posted",
    community_posted_body: "Your story is in the community feed.",
    community_encourage: "Encourage",
    community_delete: "Delete",
    community_report: "Report",
    community_share_modal: "Share with community",
    community_topic: "Topic",
    community_message: "Your message",
    community_delete_title: "Delete post",
    community_delete_body: "Remove this post from the community?",
    community_report_title: "Report post",
    community_report_body: "Report this post as harmful or spam?",
    community_report_thanks: "Thanks",
    community_report_thanks_body: "Report submitted. Repeated reports can hide a post.",
    community_could_not_delete: "Could not delete",
    community_could_not_post: "Could not post",
    community_could_not_send: "Could not send",
    community_could_not_report: "Could not report",
    community_post_btn: "Post",
    community_post_placeholder: "What helped you? What did you build? Share your experience…",
    community_encourage_title: "Encourage someone",
    community_encourage_placeholder: "A kind, short message…",
    community_send: "Send",
    community_nickname_placeholder: "e.g. QuietRiver or FocusForge22",

    habit_settings_title: "Habit settings",
    habit_name: "Habit name",
    habit_goal_days: "Goal days",
    habit_reminder: "Daily reminder (HH:MM)",
    habit_checkin: "Daily check-in notifications",
    habit_checkin_hint: "Morning and evening reminders on this device.",
    habit_save: "Save",
    habit_saving: "Saving…",
    habit_saved: "Habit settings updated.",

    home_days: "Days",
    home_quote_label: "Quote of the Day",
    home_quote_loading: "Loading today's quote…",
    home_success: "Success",
    home_fail: "Fail",
    home_urge: "Panic\nButton",
    home_best_streak: "Best streak",
    home_total_attempts: "Total attempts",
    home_success_week: "Success this week",
    home_daily_encouragement: "Daily encouragement",
    home_continue_tracker: "Continue to tracker →",

    progress_title: "PROGRESS",
    progress_add: "+ Add",
    progress_add_habit: "Add custom habit",
    progress_add_hint: "Track positive reps alongside your main streak.",
    progress_icon: "Icon",
    progress_cancel: "Cancel",
    progress_save_habit: "Save habit",
    progress_sessions: "sessions today",

    insight_title: "Insights",
    insight_success: "Success",
    insight_partial: "Partial",
    insight_gap: "Gap",
    insight_total_tracked: "Total tracked",
    insight_attempts: "Attempts",
    insight_attempts_sub: "ended by fail",
    insight_best_weekday: "Best weekday",
    insight_risk_day: "Risk day",
    insight_tracked_days: "days tracked",
    insight_consistency: "Consistency trend",
    insight_avg_14: "avg · 14 days",
    insight_active_streak: "Active streak",
    insight_best_run: "Best run",
    insight_success_rate: "Success rate",
    insight_goal_progress: "Goal progress",
    insight_of_goal: "of {days}-day goal",
    insight_recovery: "Recovery",
    insight_days_since_fail: "days since last fail",
    insight_streak_label: "Streak",
    insight_best_label: "Best",
    insight_weekday_perf: "Weekday performance",
    insight_weekday_sub: "Success-weighted score by day of week",
    insight_fail_time: "Fail by time",
    insight_fail_time_hint: "Need 3+ fails for time patterns",
    insight_top_trigger: "Top trigger",
    insight_common_relapse: "Most common relapse time",

    quote_label: "Quote of the day",
    quote_continue: "Continue to tracker →",
    quote_loading: "Loading today's quote…",
    quote_reflection_label: "Procsystim Reflection",

    welcome_tagline: "Systems over temporary motivation",
    welcome_cta: "Begin your journey",
    welcome_p1:
      "In a world where people are constantly surrounded by distractions, information overload, and short-term entertainment, many struggle to build the discipline and systems needed to achieve their goals. The problem is not always a lack of motivation, but often the absence of a structured environment that helps people develop better habits and stay consistent.",
    welcome_p2:
      "Procsystim was created with the idea that personal growth should not depend only on temporary motivation. Instead, people need practical systems that guide their daily actions, encourage learning, and help them become better versions of themselves over time.",
    welcome_p3:
      "The goal of Procsystim is to bring together valuable resources such as educational articles, inspiring videos, book recommendations, challenges, and tools that help users develop discipline, improve their mindset, and create positive routines.",
    welcome_p4:
      "By combining knowledge, technology, and personalized guidance, Procsystim aims to become a companion for people who want to transform their lives through continuous improvement and intentional action.",

    mark_success_title: "Mark as success",
    mark_success_note: "Add a note (optional)",
    mark_fail_title: "Log a setback",
    mark_fail_trigger: "What triggered it?",
    mark_fail_mood: "How do you feel?",
    mark_fail_note: "Notes (optional)",
    mark_fail_confirm: "Log setback",
    mark_cancel: "Cancel",

    urge_title: "Panic Button",
    urge_subtitle: "Ride it out. Urges peak and pass in minutes.",
    urge_timer_label: "seconds",
    urge_breathe: "Breathe slowly. This will pass.",
    urge_done: "I made it through",
    urge_close: "Close",
    urge_modal_title: "Panic Button",
    urge_modal_subtitle: "Just 60 seconds. Breathe slowly, relax your shoulders, and let the urge pass.",
    urge_modal_message: "You don't have to act on it. Urges peak and fade — you're training your brain every time you wait.",
    urge_modal_close: "Close",

    calendar_mon: "Mon",
    calendar_tue: "Tue",
    calendar_wed: "Wed",
    calendar_thu: "Thu",
    calendar_fri: "Fri",
    calendar_sat: "Sat",
    calendar_sun: "Sun",

    day_monday: "Monday",
    day_tuesday: "Tuesday",
    day_wednesday: "Wednesday",
    day_thursday: "Thursday",
    day_friday: "Friday",
    day_saturday: "Saturday",
    day_sunday: "Sunday",

    success_modal_title: "Mark today",
    success_modal_subtitle: "Choose how it went.",
    success_done_fully: "Done fully",
    success_done_partially: "Done partially",
    success_cancel: "Cancel",

    day_details_title: "Day details",
    day_details_date: "Date:",
    day_details_note_label: "Today's note",
    day_details_note_placeholder: "Write anything you want to remember about today…",
    day_details_missed: "Missed",
    day_details_relapse: "Relapse…",
    day_details_save_note: "Save note",

    fail_modal_title: "Mark fail",
    fail_modal_date: "Date:",
    fail_from_notification: "from Notifications",
    fail_time_of_day: "Time of day",
    fail_mood_label: "Mood (1–5)",
    fail_missing_trigger: "Missing trigger",
    fail_missing_trigger_body: "Pick a trigger card or write what triggered the fail.",
    fail_streak_reset_title: "Streak reset",

    milestone_3: "3 days — Your brain is starting to rebalance dopamine. Keep going.",
    milestone_7: "7 days — You may notice more energy, sharper focus, and better sleep.",
    milestone_14: "14 days — Mental clarity is improving. Old triggers feel weaker.",
    milestone_30: "30 days — Confidence, motivation, and emotional stability are growing.",
    milestone_90: "90 days — This is a major rewiring milestone. You've built real discipline.",

    trigger_title: "What triggered it?",
    trigger_hint: "Tap a common trigger, or write your own below.",
    trigger_custom_label: "Not listed? Write your own",
    trigger_custom_placeholder: "e.g. stress at work, late night scrolling…",

    streak_reset: "Reset",
    streak_active: "Active",
    streak_gap: "Gap",
    streak_missed: "Missed",
    streak_partial_label: "Partial",
    streak_runs_title: "Streak runs",
    streak_runs_sub: "Bar length = days in that run. Tap for details.",
    streak_longest: "Longest:",
    streak_attempts_label: "Attempts:",
    streak_now: "now",
    streak_scroll_hint: "↕ Scroll inside the chart for all {count} attempts",
    streak_goal_label: "goal",
    streak_active_run: "Active run",
    streak_ended_fail: "Ended on fail",
    streak_broken_gap: "Broken by gap",

    help_title: "Help",
    help_intro: "Hi — I'm Procsystim Help. I can guide you through habits, urges, passcode, blocking, and community. Ask anything.",
    help_subtitle: "Always available — not locked by passcode.",
    help_ai_connected: "Live AI is connected for richer answers.",
    help_offline: "Guided on-device help for Android v1 (live AI optional later).",
    help_placeholder: "What do you need help with?",
    help_send: "Send",
    help_chip_passcode: "Forgot passcode",
    help_chip_habit: "Add a habit",
    help_chip_block: "Block apps",
    help_chip_community: "Community",

    notif_title: "Notifications",
    notif_back: "Back",
    notif_subtitle: "Check in from notifications without opening the app. Tap Mark today as success or Mark fail directly from your lock screen.",
    notif_empty: "No notifications yet. When you use notification buttons, a log appears here.",
    notif_fail_prompt_title: "Today you hit Mark fail",
    notif_fail_prompt_body: "Do you want to tell us why? Adding context helps you track triggers.",
    notif_add_fail_details: "Add fail details",
    notif_logged: "Logged on your calendar. No extra steps needed.",

    books_teaser_title: "Transformations",
    books_see_all: "See all",
    books_top_pick: "Top pick",
    books_your_pick: "Your pick",
    books_title: "Recommended books",
    books_subtitle: "Curated reads for discipline, focus, and recovery. Tap any book for details.",
    books_add: "+ Add transformation",
    books_top_section: "TOP RECOMMENDATIONS",
    books_more_section: "MORE TO EXPLORE",
    books_by: "by",
    books_show_top: "Show in top recommendations",
    books_open_link: "Open link",
    books_remove: "Remove recommendation",
    books_add_title: "Add book recommendation",
    books_add_hint: "This appears for everyone using this install of Procsystim.",
    books_field_title: "Title *",
    books_field_title_placeholder: "Book title",
    books_field_author: "Author",
    books_field_author_placeholder: "Author name",
    books_field_why: "Why recommend it?",
    books_field_why_placeholder: "One or two sentences for your users…",
    books_field_link: "Link (optional)",
    books_top_toggle: "Top recommendation",
    books_save: "Save recommendation",

    weekday_sunday: "Sunday",
    weekday_monday: "Monday",
    weekday_tuesday: "Tuesday",
    weekday_wednesday: "Wednesday",
    weekday_thursday: "Thursday",
    weekday_friday: "Friday",
    weekday_saturday: "Saturday",

    generic_back: "Back",
    generic_cancel: "Cancel",
    generic_save: "Save",
    generic_close: "Close",
    generic_send: "Send",
    generic_done: "Done",
    generic_dismiss: "Dismiss",
    generic_loading: "Loading…",
    generic_none: "None",
    generic_na: "N/A",
  },
  fr: {
    tab_home: "Accueil",
    tab_progress: "Progrès",
    tab_insight: "Aperçus",
    tab_library: "Bibliothèque",
    tab_community: "Communauté",
    tab_screentime: "Temps d'écran",

    lang_english: "English",
    lang_french: "Français",
    lang_label: "Langue",

    menu_title: "Menu",
    menu_close: "Fermer",
    menu_help: "Aide",
    menu_notifications: "Notifications",

    library_title: "Bibliothèque",
    library_subtitle: "Articles, vidéos recommandées et livres pour accompagner votre parcours.",
    library_articles: "Articles",
    library_videos: "Vidéos recommandées",
    library_books: "Des livres qui changent des vies",
    library_back: "← Retour à la bibliothèque",
    library_open_link: "Ouvrir l'article complet",
    library_min_read: "min de lecture",
    library_empty_articles: "Aucun article dans cette langue pour le moment.",
    library_empty_videos: "Aucune vidéo dans cette langue pour le moment.",
    library_link_error_title: "Lien",
    library_link_error_body: "Impossible d'ouvrir ce lien.",

    video_loading: "Chargement de la vidéo…",
    video_unavailable: "Cette vidéo n'a pas pu être chargée.",
    video_open_youtube: "Ouvrir sur YouTube",

    community_title: "Communauté",
    community_join_title: "Rejoindre la communauté",
    community_intro_join:
      "La communauté Procsystim est réservée aux membres de l'application. Choisissez un pseudo, partagez vos expériences et encouragez ceux qui suivent le même chemin. N'utilisez jamais votre vrai nom.",
    community_intro_feed:
      "Lisez les histoires des autres membres, partagez ce que vous apprenez, et encouragez quelqu'un aujourd'hui.",
    community_signed_in: "Connecté en tant que",
    community_nickname: "Votre identifiant (pseudo)",
    community_nickname_hint: "Lettres, chiffres, underscores. Pas d'espaces. 3–24 caractères.",
    community_join_btn: "Rejoindre la communauté",
    community_joining: "Connexion…",
    community_join_error_title: "Impossible de rejoindre",
    community_nickname_taken_title: "Pseudo déjà pris",
    community_nickname_taken_body: "Ce pseudo est déjà utilisé. Essayez-en un autre.",
    community_waking_title: "Serveur en démarrage",
    community_waking_body:
      "Le serveur peut mettre jusqu'à 30 secondes à démarrer. Attendez un moment puis réessayez.",
    community_share: "+ Partager",
    community_loading: "Chargement…",
    community_back: "Retour",
    community_welcome: "Bienvenue",
    community_welcome_body:
      "Vous êtes connecté en tant que @{id}. Partagez avec sincérité — utilisez un pseudo, jamais votre vrai nom.",
    community_empty: "Aucun message pour le moment. Soyez le premier à partager votre expérience.",
    community_posted: "Publié",
    community_posted_body: "Votre histoire est dans le fil de la communauté.",
    community_encourage: "Encourager",
    community_delete: "Supprimer",
    community_report: "Signaler",
    community_share_modal: "Partager avec la communauté",
    community_topic: "Sujet",
    community_message: "Votre message",
    community_delete_title: "Supprimer le message",
    community_delete_body: "Retirer ce message de la communauté ?",
    community_report_title: "Signaler le message",
    community_report_body: "Signaler ce message comme nuisible ou indésirable ?",
    community_report_thanks: "Merci",
    community_report_thanks_body: "Signalement envoyé. Des signalements répétés peuvent masquer un message.",
    community_could_not_delete: "Impossible de supprimer",
    community_could_not_post: "Impossible de publier",
    community_could_not_send: "Impossible d'envoyer",
    community_could_not_report: "Impossible de signaler",
    community_post_btn: "Publier",
    community_post_placeholder: "Qu'est-ce qui vous a aidé ? Qu'avez-vous construit ? Partagez votre expérience…",
    community_encourage_title: "Encourager quelqu'un",
    community_encourage_placeholder: "Un court message bienveillant…",
    community_send: "Envoyer",
    community_nickname_placeholder: "ex. RivièreCalme ou ForceIntérieure22",

    habit_settings_title: "Réglages d'habitude",
    habit_name: "Nom de l'habitude",
    habit_goal_days: "Objectif (jours)",
    habit_reminder: "Rappel quotidien (HH:MM)",
    habit_checkin: "Notifications de check-in",
    habit_checkin_hint: "Rappels matin et soir sur cet appareil.",
    habit_save: "Enregistrer",
    habit_saving: "Enregistrement…",
    habit_saved: "Réglages d'habitude mis à jour.",

    home_days: "Jours",
    home_quote_label: "Citation du jour",
    home_quote_loading: "Chargement de la citation…",
    home_success: "Réussi",
    home_fail: "Échec",
    home_urge: "Bouton\nPanique",
    home_best_streak: "Meilleure série",
    home_total_attempts: "Tentatives totales",
    home_success_week: "Réussites cette semaine",
    home_daily_encouragement: "Encouragement du jour",
    home_continue_tracker: "Continuer vers le suivi →",

    progress_title: "PROGRÈS",
    progress_add: "+ Ajouter",
    progress_add_habit: "Ajouter une habitude",
    progress_add_hint: "Suivez des actions positives en plus de votre série principale.",
    progress_icon: "Icône",
    progress_cancel: "Annuler",
    progress_save_habit: "Enregistrer",
    progress_sessions: "sessions aujourd'hui",

    insight_title: "Aperçus",
    insight_success: "Réussi",
    insight_partial: "Partiel",
    insight_gap: "Vide",
    insight_total_tracked: "Jours suivis",
    insight_attempts: "Tentatives",
    insight_attempts_sub: "terminées par un échec",
    insight_best_weekday: "Meilleur jour",
    insight_risk_day: "Jour à risque",
    insight_tracked_days: "jours suivis",
    insight_consistency: "Tendance de régularité",
    insight_avg_14: "moy. · 14 jours",
    insight_active_streak: "Série en cours",
    insight_best_run: "Meilleure série",
    insight_success_rate: "Taux de réussite",
    insight_goal_progress: "Progression vers l'objectif",
    insight_of_goal: "sur un objectif de {days} jours",
    insight_recovery: "Rétablissement",
    insight_days_since_fail: "jours depuis le dernier échec",
    insight_streak_label: "Série",
    insight_best_label: "Record",
    insight_weekday_perf: "Performance par jour",
    insight_weekday_sub: "Score pondéré par jour de la semaine",
    insight_fail_time: "Échecs par moment",
    insight_fail_time_hint: "Il faut 3 échecs ou plus pour voir les tendances",
    insight_top_trigger: "Déclencheur principal",
    insight_common_relapse: "Moment de rechute le plus fréquent",

    quote_label: "Citation du jour",
    quote_continue: "Continuer vers le suivi →",
    quote_loading: "Chargement de la citation…",
    quote_reflection_label: "Réflexion Procsystim",

    welcome_tagline: "Des systèmes plutôt qu'une motivation temporaire",
    welcome_cta: "Commencer le parcours",
    welcome_p1:
      "Dans un monde saturé de distractions, de surcharge d'information et de divertissements à court terme, beaucoup peinent à bâtir la discipline et les systèmes nécessaires pour atteindre leurs objectifs. Le problème n'est pas toujours un manque de motivation, mais souvent l'absence d'un environnement structuré qui aide à développer de meilleures habitudes et à rester constant.",
    welcome_p2:
      "Procsystim a été créé avec l'idée que la croissance personnelle ne doit pas dépendre uniquement d'une motivation temporaire. Les gens ont besoin de systèmes concrets qui guident leurs actions quotidiennes, encouragent l'apprentissage et les aident à devenir de meilleures versions d'eux-mêmes.",
    welcome_p3:
      "L'objectif de Procsystim est de rassembler des ressources utiles : articles éducatifs, vidéos inspirantes, recommandations de livres, défis et outils pour développer la discipline, améliorer l'état d'esprit et créer des routines positives.",
    welcome_p4:
      "En combinant savoir, technologie et accompagnement personnalisé, Procsystim vise à devenir un compagnon pour celles et ceux qui veulent transformer leur vie par l'amélioration continue et l'action intentionnelle.",

    mark_success_title: "Marquer comme réussi",
    mark_success_note: "Ajouter une note (optionnel)",
    mark_fail_title: "Enregistrer un écart",
    mark_fail_trigger: "Qu'est-ce qui l'a déclenché ?",
    mark_fail_mood: "Comment vous sentez-vous ?",
    mark_fail_note: "Notes (optionnel)",
    mark_fail_confirm: "Enregistrer l'écart",
    mark_cancel: "Annuler",

    urge_title: "Bouton Panique",
    urge_subtitle: "Tenez bon. L'envie passe en quelques minutes.",
    urge_timer_label: "secondes",
    urge_breathe: "Respirez lentement. Ça va passer.",
    urge_done: "J'ai tenu bon",
    urge_close: "Fermer",
    urge_modal_title: "Bouton Panique",
    urge_modal_subtitle: "60 secondes. Respirez lentement, relâchez vos épaules et laissez l'envie passer.",
    urge_modal_message: "Vous n'avez pas à céder. Les envies montent puis redescendent — vous entraînez votre cerveau chaque fois que vous résistez.",
    urge_modal_close: "Fermer",

    calendar_mon: "Lun",
    calendar_tue: "Mar",
    calendar_wed: "Mer",
    calendar_thu: "Jeu",
    calendar_fri: "Ven",
    calendar_sat: "Sam",
    calendar_sun: "Dim",

    day_monday: "Lundi",
    day_tuesday: "Mardi",
    day_wednesday: "Mercredi",
    day_thursday: "Jeudi",
    day_friday: "Vendredi",
    day_saturday: "Samedi",
    day_sunday: "Dimanche",

    success_modal_title: "Marquer aujourd'hui",
    success_modal_subtitle: "Comment s'est passée la journée ?",
    success_done_fully: "Journée réussie",
    success_done_partially: "Partiellement réussi",
    success_cancel: "Annuler",

    day_details_title: "Détails du jour",
    day_details_date: "Date :",
    day_details_note_label: "Note du jour",
    day_details_note_placeholder: "Écrivez ce que vous voulez retenir de cette journée…",
    day_details_missed: "Manqué",
    day_details_relapse: "Rechute…",
    day_details_save_note: "Enregistrer la note",

    fail_modal_title: "Enregistrer un échec",
    fail_modal_date: "Date :",
    fail_from_notification: "depuis les notifications",
    fail_time_of_day: "Moment de la journée",
    fail_mood_label: "Humeur (1–5)",
    fail_missing_trigger: "Déclencheur manquant",
    fail_missing_trigger_body: "Choisissez un déclencheur ou décrivez ce qui a causé l'échec.",
    fail_streak_reset_title: "Série réinitialisée",

    milestone_3: "3 jours — Votre cerveau commence à rééquilibrer la dopamine. Continuez.",
    milestone_7: "7 jours — Vous remarquerez peut-être plus d'énergie, une meilleure concentration et un meilleur sommeil.",
    milestone_14: "14 jours — La clarté mentale s'améliore. Les anciens déclencheurs perdent de leur force.",
    milestone_30: "30 jours — La confiance, la motivation et la stabilité émotionnelle grandissent.",
    milestone_90: "90 jours — C'est une étape majeure de reprogrammation. Vous avez bâti une vraie discipline.",

    trigger_title: "Qu'est-ce qui l'a déclenché ?",
    trigger_hint: "Appuyez sur un déclencheur courant ou écrivez le vôtre ci-dessous.",
    trigger_custom_label: "Pas dans la liste ? Écrivez le vôtre",
    trigger_custom_placeholder: "ex. stress au travail, navigation tardive…",

    streak_reset: "Réinitialisation",
    streak_active: "En cours",
    streak_gap: "Pause",
    streak_missed: "Manqué",
    streak_partial_label: "Partiel",
    streak_runs_title: "Historique des séries",
    streak_runs_sub: "Longueur = jours dans la série. Appuyez pour plus de détails.",
    streak_longest: "Plus longue :",
    streak_attempts_label: "Tentatives :",
    streak_now: "en cours",
    streak_scroll_hint: "↕ Faites défiler pour voir les {count} tentatives",
    streak_goal_label: "objectif",
    streak_active_run: "Série en cours",
    streak_ended_fail: "Terminée par un échec",
    streak_broken_gap: "Interrompue par une pause",

    help_title: "Aide",
    help_intro: "Bonjour — je suis l'Aide Procsystim. Je peux vous guider sur les habitudes, les envies, le code d'accès, le blocage et la communauté. Posez vos questions.",
    help_subtitle: "Toujours disponible — non verrouillé par le code d'accès.",
    help_ai_connected: "L'IA est connectée pour des réponses plus complètes.",
    help_offline: "Aide guidée sur l'appareil pour Android v1 (IA optionnelle plus tard).",
    help_placeholder: "Comment puis-je vous aider ?",
    help_send: "Envoyer",
    help_chip_passcode: "Code oublié",
    help_chip_habit: "Ajouter une habitude",
    help_chip_block: "Bloquer des apps",
    help_chip_community: "Communauté",

    notif_title: "Notifications",
    notif_back: "Retour",
    notif_subtitle: "Enregistrez depuis les notifications sans ouvrir l'app. Appuyez sur Marquer réussi ou Marquer échec directement depuis l'écran de verrouillage.",
    notif_empty: "Pas encore de notifications. Quand vous utilisez les boutons de notification, un historique apparaît ici.",
    notif_fail_prompt_title: "Aujourd'hui vous avez appuyé sur Marquer échec",
    notif_fail_prompt_body: "Voulez-vous nous dire pourquoi ? Ajouter du contexte aide à suivre les déclencheurs.",
    notif_add_fail_details: "Ajouter des détails",
    notif_logged: "Enregistré dans votre calendrier. Aucune action supplémentaire nécessaire.",

    books_teaser_title: "Transformations",
    books_see_all: "Voir tout",
    books_top_pick: "Recommandé",
    books_your_pick: "Votre choix",
    books_title: "Livres recommandés",
    books_subtitle: "Lectures choisies pour la discipline, la concentration et le rétablissement. Appuyez sur un livre pour plus de détails.",
    books_add: "+ Ajouter une transformation",
    books_top_section: "MEILLEURES RECOMMANDATIONS",
    books_more_section: "À DÉCOUVRIR",
    books_by: "par",
    books_show_top: "Afficher dans les recommandations",
    books_open_link: "Ouvrir le lien",
    books_remove: "Retirer la recommandation",
    books_add_title: "Ajouter un livre",
    books_add_hint: "Ceci apparaît pour tous les utilisateurs de cette installation de Procsystim.",
    books_field_title: "Titre *",
    books_field_title_placeholder: "Titre du livre",
    books_field_author: "Auteur",
    books_field_author_placeholder: "Nom de l'auteur",
    books_field_why: "Pourquoi le recommander ?",
    books_field_why_placeholder: "Une ou deux phrases pour vos utilisateurs…",
    books_field_link: "Lien (optionnel)",
    books_top_toggle: "Recommandation principale",
    books_save: "Enregistrer la recommandation",

    weekday_sunday: "Dimanche",
    weekday_monday: "Lundi",
    weekday_tuesday: "Mardi",
    weekday_wednesday: "Mercredi",
    weekday_thursday: "Jeudi",
    weekday_friday: "Vendredi",
    weekday_saturday: "Samedi",

    generic_back: "Retour",
    generic_cancel: "Annuler",
    generic_save: "Enregistrer",
    generic_close: "Fermer",
    generic_send: "Envoyer",
    generic_done: "Terminé",
    generic_dismiss: "Fermer",
    generic_loading: "Chargement…",
    generic_none: "Aucun",
    generic_na: "N/D",
  },
};
